/**
 * Parses a partial JSON string for use with streaming API responses
 * @param jsonStr
 * @returns Parsed JSON, or empty obeject until first key/value is encountered
 */

class PartialJSONParser {
  private lastIndex = 0
  private stack: string[] = [] // Track open objects and arrays
  private quoteOpen = false
  private commaEncountered = false // Whether we've encountered a comma since the last object or array
  private keyOpen = false // Whether we're currently parsing a key
  private tokensRemoved: string[] = [] // Track tokens (e.g. "{" "[") that were removed from the JSON string

  parse(jsonStr: string): Record<string, any> | Record<string, any>[] {
    this.tokensRemoved = []

    for (let i = this.lastIndex; i < jsonStr.length; i++) {
      const char = jsonStr[i]

      if (char === '"' && jsonStr[i - 1] !== '\\') {
        this.quoteOpen = !this.quoteOpen
      }

      if (!this.quoteOpen) {
        if (char === '{' || char === '[') {
          this.stack.push(char)
          this.commaEncountered = false
          this.keyOpen = false
        } else if (char === '}' && this.stack[this.stack.length - 1] === '{') {
          this.stack.pop()
        } else if (char === ']' && this.stack[this.stack.length - 1] === '[') {
          this.stack.pop()
          this.commaEncountered = false
        } else if (char === ':') {
          this.keyOpen = true
          this.commaEncountered = false
        } else if (char === ',') {
          this.keyOpen = false
          this.commaEncountered = true
        }
      }
    }

    // Update the index so we know where to start parsing from next time
    this.lastIndex = jsonStr.length

    /**
     *
     * Everything below this point makes incomplete JSON valid.
     *
     * For example, if the last character is a comma, or if there is
     * an incomplete object, or if there are missing closing brackets.
     *
     */

    jsonStr = jsonStr.trim()

    if (this.quoteOpen) {
      jsonStr += '"'
    }

    const inArray = this.stack[this.stack.length - 1] === '['

    // In the case of partial key definition, we roll back to the previous comma
    // e.g. { "key": "
    // e.g. { "key": "v
    // e.g. { "key": "v"
    if (
      (this.commaEncountered && !inArray) ||
      jsonStr.endsWith(':') ||
      (!this.keyOpen && jsonStr.endsWith('"'))
    ) {
      const lastCommaIndex = jsonStr.lastIndexOf(',')
      if (lastCommaIndex !== -1) {
        // Get the last comma index and save everything after it
        const afterComma = jsonStr.substring(lastCommaIndex + 1)

        // Pop any objects off the stack that were opened after the last comma
        for (let i = 0; i < afterComma.length; i++) {
          if (afterComma[i] === '{' || afterComma[i] === '[') {
            this.tokensRemoved.push(afterComma[i])
          }
        }

        // Get the last comma index and remove everything after it
        jsonStr = jsonStr.substring(0, lastCommaIndex)
      }
    }

    if (jsonStr.endsWith(',')) {
      jsonStr = jsonStr.substring(0, jsonStr.length - 1)
    }

    // Add necessary closing brackets based on the current state of the stack
    for (let i = this.stack.length - 1; i >= 0; i--) {
      if (this.stack[i] === this.tokensRemoved[this.tokensRemoved.length - 1]) {
        this.tokensRemoved.pop()
        continue
      }
      if (this.stack[i] === '{') {
        jsonStr += '}'
      } else if (this.stack[i] === '[') {
        jsonStr += ']'
      }
    }

    try {
      return JSON.parse(jsonStr)
    } catch (e) {
      return {}
    }
  }

  reset() {
    this.lastIndex = 0
    this.stack = []
    this.quoteOpen = false
    this.commaEncountered = false
    this.keyOpen = false
    this.tokensRemoved = []
  }
}

export default PartialJSONParser
